import { showQuotaDialogOnDenial } from "./prediction-quota-dialog";
import { preparePythonPrediction } from "./python-prediction-request";
import {
  AppendTextToPromptArgs,
  AppendTextToPromptResponse,
  AutoCorrectCustomModelCaptionArgs,
  AutoCorrectCustomModelCaptionResponse,
  CreateCustomModelArgs,
  DeleteCustomModelArgs,
  DeleteCustomModelPredictionArgs,
  DeleteCustomModelPredictionResponse,
  DeleteCustomModelResponse,
  DeleteSingleOutputPredictionArgs,
  FixCustomModelDetailsCorrespondenceInput,
  FixCustomModelDetailsCorrespondenceOutput,
  GetAllUserCustomModelPredictionsArgs, // <--- Add this
  GetCustomModelTrainingArgs,
  GetCustomModelTrainingsArgs,
  GetCustomModelTrainingsResponse,
  GetPublicCustomModelPredictionsArgs,
  GetPublicCustomModelPredictionsResponse,
  GetTrainingCaptionShortenedArgs,
  GetTrainingCaptionShortenedResponse,
  isFixCustomModelDetailsCorrespondenceOutput,
  OnAllUserCustomModelPredictionsUpdateArgs, // <--- Add this
  OnCustomModelPredictionsUpdateArgs,
  OnCustomModelPredictionUpdateArgs,
  OnCustomModelTrainingCollectionUpdateArgs,
  OnCustomModelTrainingUpdateArgs,
  OnUserCustomModelsUpdateArgs,
  RecaptionCustomModelTrainingArgs,
  RecaptionCustomModelTrainingResponse,
  RemoveTextFromPromptArgs,
  RemoveTextFromPromptResponse,
  StartCustomModelPredictionArgs,
  StartCustomModelTrainingArgs,
  StopCustomModelPredictionArgs,
  StopCustomModelTrainingArgs,
  UpdateCustomModelDatasetItemThumbnailArgs,
  UpdateCustomModelDatasetItemThumbnailResponse,
  UpdateCustomModelInfoArgs,
  UpdateUserModifedTrainingCaptionArgs,
  UploadCustomModelAssetToStorageArgs,
  UploadCustomModelAssetToStorageResponse,
  UploadCustomModelDataItemToStorageArgs,
  UploadCustomModelDataItemToStorageResponse,
  UploadCustomModelVirtualTryOnInputToStorageArgs,
  UploadCustomModelVirtualTryOnInputToStorageResponse,
} from "@/backend/base";
import {
  ChangeCameraAngleInput,
  ChangeCameraAngleResponse,
  ChangeColorRequest,
  ChangeColorResponse,
  ChangePoseInput,
  ChangePoseResponse,
  ClarityUpscaleInput,
  ClarityUpscaleResponse,
  CustomModelPostProcessActionType,
  CustomModelPostProcessInput,
  CustomModelPostProcessPythonInput,
  CustomModelPostProcessPythonResponse,
  CustomModelPostProcessResponse,
  DepthControlnetInput,
  DepthControlnetResponse,
  ExecuteMultiStepNodeWorkflowArgs,
  ExecuteMultiStepNodeWorkflowResponse,
  FaceUpscalerInput,
  FaceUpscalerResponse,
  FixProductDetailsInput,
  FixProductDetailsResponse,
  FluxControlnetInput,
  FluxControlnetResponse,
  GetMaskFromPromptInput,
  GetMaskFromPromptResponse,
  ICLightInput,
  ICLightResponse,
  IdeogramInpaintingRequest,
  IdeogramInpaintingResponse,
  IdeogramRemixRequest,
  IdeogramRemixResponse,
  IdeogramText2ImageRequest,
  IdeogramText2ImageResponse,
  InContextVariationsInput,
  InContextVariationsResponse,
  MagicEraseInput,
  MagicEraseResponse,
  MultiStageGenerationInput,
  MultiStageGenerationResponse,
  SwapFaceInput,
  SwapFaceResponse,
  TryOnInput,
  TryOnResponse,
} from "@/backend/custom-model-post-process";
import { FirebaseBackend } from "@/backend/firebase/firebase-backend";
import {
  appendPromptInstructions,
  removePromptInstructions,
} from "@/components/custom-model/custom-model-prompt-instructions";
import { extractModifiedPrompt } from "@/components/custom-model/custom-model-prompt-utils";
import { editorContextStore } from "@/contexts/editor-context";
import {
  AppRoleType,
  EditorAssetContentType,
  PublicUserId,
  UiDisplayMessageDialogEventHandler,
  UserAssetType,
} from "@/core/common/types";
import { CustomModelPredictionType } from "@/core/common/types/custom-model-prediction-type";
import {
  InstantCustomModelGenerationArgs,
  InstantCustomModelGenerationResponse,
} from "@/core/common/types/custom-model-predition-instant";
import { isCustomModelScaleConfigs } from "@/core/common/types/custom-model-scale-configs";
import {
  correctCustomModelPredictionInputScaleConfigs,
  CustomModelAction,
  CustomModelDataset,
  CustomModelDatasetItem,
  CustomModelInfo,
  CustomModelPredictionItem,
  CustomModelScaleConfigs,
  CustomModelTrainingBackendType,
  CustomModelTrainingItem,
  CustomModelType,
  HandleCreateCustomModelArgs,
  HandleCreateCustomModelResponse,
  HandleCustomModelTrainingStartArgs,
  HandleCustomModelTrainingStartResponse,
  HandleCustomModelTrainingStopArgs,
  HandleCustomModelTrainingStopResponse,
  HandleStartCustomModelPredictionArgs,
  HandleStartCustomModelPredictionResponse,
  HandleStopCustomModelPredictionArgs,
  HandleStopCustomModelPredictionResponse,
  isCustomModelDatasetItem,
  isCustomModelInfo,
  isCustomModelPredictionItem,
  isCustomModelPredictionInputFal,
  isCustomModelPredictionInputSelfHost,
  isCustomModelTrainingItem,
} from "@/core/common/types/custom-model-types";
import { DocVisibility } from "@/core/common/types/doc-visibility";
import { isPublicTeamId, PublicTeamId } from "@/core/common/types/team";
import {
  canUserCreateCustomModel,
  canUserRunCustomModelPostProcessing,
  CanUserRunCustomModelPostProcessingResponseNotAllowed,
  canUserStartPrediction,
  canUserStartTraining,
} from "@/core/utils/custom-model-utils";
import { removeUndefinedFromObject } from "@/core/utils/object-utils";
import { debugError, debugLog } from "@/core/utils/print-utilts";
import { canUserUploadAsset } from "@/core/utils/quota-utils";
import { extractTripleTickContent, isDataURL, isValidHttpsUrl } from "@/core/utils/string-utils";
import { getCurrentTeamId } from "@/core/utils/team-utils";
import { isRoleTeamMember } from "@/core/utils/user-role-utils";
import { getUserQuotasFromEditorContext } from "@/hooks/use-user-quotas";
import debounce from "debounce-promise";
import {
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  limit as limitConstraint, // for concise usage with "limit"
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { Functions, httpsCallable, HttpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import {
  confirmRenderAdmissionRequest,
  getRenderAdmissionRequest,
  renderAdmissionFingerprint,
} from "./render-admission-request";
import {
  CustomModelPostProcessV2ActionType,
  CustomModelPostProcessV2Input,
  CustomModelPostProcessV2Response,
} from "../custom-model-node-workflow";
import {
  createGenerateUserAssetUploadUrlFunction,
  GenerateUserAssetUploadUrlArgs,
  GenerateUserAssetUploadUrlFunction,
  GenerateUserAssetUploadUrlResponse,
} from "./asset-upload-utils";
import { ChatWithImagesFunction, createChatWithImagesFunction } from "./chat-with-images-utils";
import {
  AUTO_CORRECT_CUSTOM_MODEL_CAPTION,
  CUSTOM_MODEL_ACTION_ENTRYPOINT,
  CUSTOM_MODEL_POST_PROCESS_PYTHON,
  EXECUTE_MULTI_STEP_NODE_WORKFLOW,
  FIX_CUSTOM_MODEL_DETAILS_CORRESPONDENCE,
  GET_TRAINING_CAPTION_SHORTENED,
  INSTANT_CUSTOM_MODEL_GENERATION,
  RECAPTION_CUSTOM_MODEL_TRAINING,
  UPDATE_CUSTOM_MODEL_DATASET_ITEM_THUMBNAIL,
} from "./firebase-function-name";
import { onSnapshotWithExistenceCheck } from "./on-snapshot-utils";
import { getUploadContentType } from "./storage/upload-validation";
import { getFirebaseIdTokenHeader } from "@/backend/firebase/firebase-id-token-header";
// Product types are defined here for reference, for multi-stage generation we want to separate models based on their type
const CUSTOM_MODEL_PRODUCT_TYPES = new Set([
  CustomModelType.Product,
  CustomModelType.Fashion,
  CustomModelType.Furniture,
  CustomModelType.Tech,
  CustomModelType.Vase,
  CustomModelType.Footwear,
  CustomModelType.Jewelry,
  CustomModelType.Bags,
  CustomModelType.BrandA,
  CustomModelType.BrandB,
  CustomModelType.Custom,
]);

export const customModelCollectionName = "customModelsV2";
export const customModelDatasetCollectionName = "dataset";
export const customModelTrainingCollectionName = "training";
export const customModelPredictionCollectionName = "customModelsV2Predictions";

export function getCustomModelCollectionRef(firestore: Firestore) {
  return collection(firestore, customModelCollectionName);
}

export function getCustomModelDocRef({
  firestore,
  modelId,
}: {
  firestore: Firestore;
  modelId: string;
}) {
  return doc(getCustomModelCollectionRef(firestore), modelId);
}

export function getCustomModelDatasetCollectionRef({
  firestore,
  modelId,
}: {
  firestore: Firestore;
  modelId: string;
}) {
  return collection(getCustomModelDocRef({ firestore, modelId }), customModelDatasetCollectionName);
}

export function getCustomModelDatasetItemDocRef({
  firestore,
  modelId,
  dataId,
}: {
  firestore: Firestore;
  modelId: string;
  dataId: string;
}) {
  return doc(
    getCustomModelDatasetCollectionRef({
      firestore,
      modelId,
    }),
    dataId,
  );
}

export function getCustomModelTrainingCollectionRef({
  firestore,
  modelId,
}: {
  firestore: Firestore;
  modelId: string;
}) {
  return collection(
    getCustomModelDocRef({ firestore, modelId }),
    customModelTrainingCollectionName,
  );
}

export function getCustomModelTrainingDocRef({
  firestore,
  modelId,
  trainingId,
}: {
  firestore: Firestore;
  modelId: string;
  trainingId: string;
}) {
  return doc(
    getCustomModelTrainingCollectionRef({
      firestore,
      modelId,
    }),
    trainingId,
  );
}

function getCustomModelPredictionCollectionRef({ firestore }: { firestore: Firestore }) {
  return collection(firestore, customModelPredictionCollectionName);
}

function getCustomModelPredictionDocRef({
  firestore,
  predictionId,
}: {
  firestore: Firestore;
  predictionId: string;
}) {
  return doc(
    getCustomModelPredictionCollectionRef({
      firestore,
    }),
    predictionId,
  );
}

function getStoragePathFromSignedUrl(signedUrl: string): string {
  try {
    const url = new URL(signedUrl);
    const prefix = `/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/`;
    const pathname = url.pathname;
    if (pathname.startsWith(prefix)) {
      return pathname.slice(prefix.length);
    }
    return pathname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "";
  }
}

async function getUploadDataFromString(body: string) {
  if (isDataURL(body) || isValidHttpsUrl(body)) {
    const response = await fetch(body);
    if (!response.ok) {
      throw new Error(`Failed to fetch data from URI. Status: ${response.status}`);
    }
    return await response.blob();
  }
  return body;
}

/**
 * Uploads a string/blob/file to cloud storage using a signed URL and sets custom metadata.
 * Used primarily for custom model data uploads.
 *
 * @example
 * const resp = await uploadDataWithMetadata(signedUrl, data, "image/png", { "x-goog-meta-myTag": "abc" });
 */
async function uploadDataWithMetadata(
  signedUrl: string,
  body: string | File | Blob,
  contentType: string,
  metadata: Record<string, string>,
): Promise<Response> {
  if (!body) {
    throw new Error("Upload body is empty or undefined");
  }

  try {
    let uploadBody = body;
    if (typeof body === "string" && !isDataURL(body) && !isValidHttpsUrl(body)) {
      uploadBody = new Blob([body], { type: contentType });
    }

    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        ...metadata,
      },
      body: uploadBody,
      credentials: "omit",
      mode: "cors",
    });

    if (!response.ok) {
      const errorText = await response.text();
      debugError(`Upload failed with status: ${response.status} - ${errorText}`);
      throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error("Error uploading data with metadata:", error);
    throw error;
  }
}

export class CustomModelManager {
  private firestore: Firestore;

  private backend: FirebaseBackend;

  private chatWithImages: ChatWithImagesFunction;

  private cachedPublicCustomModels: CustomModelInfo[] = [];

  private customModelActionEntrypoint: HttpsCallable<
    | HandleCreateCustomModelArgs
    | HandleCustomModelTrainingStartArgs
    | HandleCustomModelTrainingStopArgs
    | HandleStartCustomModelPredictionArgs
    | HandleStopCustomModelPredictionArgs,
    | HandleCreateCustomModelResponse
    | HandleCustomModelTrainingStartResponse
    | HandleCustomModelTrainingStopResponse
    | HandleStartCustomModelPredictionResponse
    | HandleStopCustomModelPredictionResponse
  >;

  private fixCustomModelDetailsCorrespondence: HttpsCallable<
    FixCustomModelDetailsCorrespondenceInput,
    FixCustomModelDetailsCorrespondenceOutput
  >;

  private generateUserAssetUploadUrlColabJuly24: GenerateUserAssetUploadUrlFunction;

  private autoCorrectCustomModelCaptionColabJuly24: HttpsCallable<
    AutoCorrectCustomModelCaptionArgs,
    AutoCorrectCustomModelCaptionResponse
  >;

  private recaptionCustomModelTrainingColabJuly24: HttpsCallable<
    RecaptionCustomModelTrainingArgs,
    RecaptionCustomModelTrainingResponse
  >;

  private getTrainingCaptionShortenedColabJuly24: HttpsCallable<
    GetTrainingCaptionShortenedArgs,
    GetTrainingCaptionShortenedResponse
  >;

  private updateCustomModelDatasetItemThumbnailColabJuly24: HttpsCallable<
    UpdateCustomModelDatasetItemThumbnailArgs,
    UpdateCustomModelDatasetItemThumbnailResponse
  >;

  // NOTE(Leon): this used to be for multi-step node workflow specifically but now it's just for any multi node workflow, and I didn't want to mess with the name. sorry future me or you or whoever is reading this.
  private executeMultiStepNodeWorkflowColabJuly24: HttpsCallable<
    ExecuteMultiStepNodeWorkflowArgs,
    ExecuteMultiStepNodeWorkflowResponse
  >;

  private instantCustomModelGenerationColabJuly24: HttpsCallable<
    InstantCustomModelGenerationArgs,
    InstantCustomModelGenerationResponse
  >;

  private customModelPostProcessPythonColabJuly24: HttpsCallable<
    CustomModelPostProcessPythonInput,
    CustomModelPostProcessPythonResponse
  >;

  constructor({
    firestore,
    firebaseFunctions,
    backend,
  }: {
    firestore: Firestore;
    firebaseFunctions: Functions;
    backend: FirebaseBackend;
  }) {
    this.firestore = firestore;
    this.backend = backend;

    this.customModelActionEntrypoint = httpsCallable(
      firebaseFunctions,
      CUSTOM_MODEL_ACTION_ENTRYPOINT,
    );

    this.fixCustomModelDetailsCorrespondence = httpsCallable(
      firebaseFunctions,
      FIX_CUSTOM_MODEL_DETAILS_CORRESPONDENCE,
    );

    this.autoCorrectCustomModelCaptionColabJuly24 = httpsCallable(
      firebaseFunctions,
      AUTO_CORRECT_CUSTOM_MODEL_CAPTION,
    );

    this.recaptionCustomModelTrainingColabJuly24 = httpsCallable(
      firebaseFunctions,
      RECAPTION_CUSTOM_MODEL_TRAINING,
    );

    this.getTrainingCaptionShortenedColabJuly24 = httpsCallable(
      firebaseFunctions,
      GET_TRAINING_CAPTION_SHORTENED,
    );

    this.updateCustomModelDatasetItemThumbnailColabJuly24 = httpsCallable(
      firebaseFunctions,
      UPDATE_CUSTOM_MODEL_DATASET_ITEM_THUMBNAIL,
    );

    this.generateUserAssetUploadUrlColabJuly24 = createGenerateUserAssetUploadUrlFunction({
      firebaseFunctions,
    });

    this.chatWithImages = createChatWithImagesFunction({
      firebaseFunctions,
    });

    this.executeMultiStepNodeWorkflowColabJuly24 = httpsCallable(
      firebaseFunctions,
      EXECUTE_MULTI_STEP_NODE_WORKFLOW,
      { timeout: 600 * 1000 }, // 600 seconds
    );

    this.instantCustomModelGenerationColabJuly24 = httpsCallable(
      firebaseFunctions,
      INSTANT_CUSTOM_MODEL_GENERATION,
      { timeout: 600 * 1000 }, // 600 seconds
    );

    this.customModelPostProcessPythonColabJuly24 = httpsCallable(
      firebaseFunctions,
      CUSTOM_MODEL_POST_PROCESS_PYTHON,
    );
  }

  private getCustomModelCollectionRef() {
    return getCustomModelCollectionRef(this.firestore);
  }

  private getCustomModelDocRef(modelId: string) {
    return getCustomModelDocRef({
      firestore: this.firestore,
      modelId,
    });
  }

  private getCustomModelDatasetRef(modelId: string) {
    return getCustomModelDatasetCollectionRef({
      firestore: this.firestore,
      modelId,
    });
  }

  private getCustomModelTrainingColectionRef(modelId: string) {
    return getCustomModelTrainingCollectionRef({
      firestore: this.firestore,
      modelId,
    });
  }

  private getCustomModelTrainingDocRef({
    modelId,
    trainingId,
  }: {
    modelId: string;
    trainingId: string;
  }) {
    return getCustomModelTrainingDocRef({
      firestore: this.firestore,
      modelId,
      trainingId,
    });
  }

  private getCustomModelPredictionCollectionRef() {
    return getCustomModelPredictionCollectionRef({
      firestore: this.firestore,
    });
  }

  private getCustomModelPredictionDocRef({ predictionId }: { predictionId: string }) {
    return getCustomModelPredictionDocRef({
      firestore: this.firestore,
      predictionId,
    });
  }

  private getCustomModelDatasetItemRef(modelId: string, dataId: string) {
    return getCustomModelDatasetItemDocRef({
      firestore: this.firestore,
      dataId,
      modelId,
    });
  }

  private static getCustomModelDatasetFromDocs<T>(
    docs: QueryDocumentSnapshot<T>[],
  ): CustomModelDataset {
    const output: CustomModelDataset = {};
    docs.forEach((doc) => {
      const item = doc.data();
      if (isCustomModelDatasetItem(item)) {
        output[doc.id] = item;
      }
    });
    return output;
  }

  /**
   * Fetch all dataset items for a given custom model.
   * @returns A dictionary of dataset items, keyed by doc ID.
   *
   * @example
   * const dataset = await manager.getCustomModelDataset("myModel123");
   * console.log(dataset["someDataId"]);
   */
  getCustomModelDataset(modelId: string): Promise<CustomModelDataset> {
    return getDocs(this.getCustomModelDatasetRef(modelId))
      .then((snap) => snap.docs)
      .then(CustomModelManager.getCustomModelDatasetFromDocs);
  }

  onCustomModelDatasetUpdate(modelId: string, callback: (dataset?: CustomModelDataset) => void) {
    return onSnapshot(this.getCustomModelDatasetRef(modelId), (snapshot) => {
      callback(CustomModelManager.getCustomModelDatasetFromDocs(snapshot.docs));
    });
  }

  async setCustomModelDataItem({
    modelId,
    dataId,
    data,
  }: {
    modelId: string;
    dataId: string;
    data: Partial<CustomModelDatasetItem>;
  }) {
    data = {
      ...data,
      timeCreated: serverTimestamp() as Timestamp,
      timeModified: serverTimestamp() as Timestamp,
    };

    if (!data?.id || !data?.storagePath) {
      debugError(`Cannot set model ${modelId} data ${dataId}: `, data);
      return;
    }

    return setDoc(
      this.getCustomModelDatasetItemRef(modelId, dataId),
      removeUndefinedFromObject(data),
    );
  }

  updateCustomModelDataItem({
    modelId,
    dataId,
    data,
  }: {
    modelId: string;
    dataId: string;
    data: Partial<CustomModelDatasetItem>;
  }) {
    data = {
      ...data,
      timeModified: serverTimestamp() as Timestamp,
    };

    return updateDoc(
      this.getCustomModelDatasetItemRef(modelId, dataId),
      removeUndefinedFromObject(data),
    );
  }

  async deleteCustomModelDataItem({ modelId, dataId }: { modelId: string; dataId: string }) {
    debugLog(`Delete model ${modelId} data ${dataId}`);
    return await deleteDoc(this.getCustomModelDatasetItemRef(modelId, dataId));
  }

  onCustomModelDatasetItemUpdate(
    modelId: string,
    dataId: string,
    callback: (dataItem?: CustomModelDatasetItem) => void,
  ) {
    return onSnapshot(this.getCustomModelDatasetItemRef(modelId, dataId), (snapshot) => {
      const data = snapshot.data();
      if (isCustomModelDatasetItem(data)) {
        callback(data);
      } else {
        callback(undefined);
      }
    });
  }

  private async generateAssetUploadUrl({
    assetType,
    contentType,
    sizeBytes,
    tags,
    extensionHeaders,
  }: GenerateUserAssetUploadUrlArgs): Promise<GenerateUserAssetUploadUrlResponse> {
    const currentTeamId = getCurrentTeamId();
    const response = await this.generateUserAssetUploadUrlColabJuly24({
      assetType,
      contentType,
      sizeBytes,
      publicTeamId: currentTeamId,
      tags,
      extensionHeaders,
    });

    const { assetId, signedUrl, extensionHeaders: responseExtensionHeaders } = response.data ?? {};
    return {
      assetId,
      signedUrl,
      extensionHeaders: responseExtensionHeaders,
    };
  }

  /**
   * Uploads arbitrary file data to custom model storage. If successful, returns the storage path.
   * @example
   * const path = await manager.uploadAssetToStorage({ data: someBlob, assetType: UserAssetType.CustomModelDatasetItem, ... });
   */
  async uploadAssetToStorage({
    data,
    assetType,
    tags,
    extensionHeaders,
    publicTeamId,
  }: UploadCustomModelAssetToStorageArgs): Promise<UploadCustomModelAssetToStorageResponse> {
    try {
      const contentType = await getUploadContentType(data, data.type as EditorAssetContentType);
      const {
        assetId,
        signedUrl,
        extensionHeaders: responseExtensionHeaders,
      } = await this.generateAssetUploadUrl({
        assetType,
        contentType,
        sizeBytes: data.size,
        tags,
        extensionHeaders,
        publicTeamId,
      });

      if (!signedUrl || !assetId) {
        return;
      }

      const storagePath = getStoragePathFromSignedUrl(signedUrl);
      if (!storagePath) {
        return undefined;
      }

      const headers = Object.fromEntries(
        Object.entries(responseExtensionHeaders).filter(([, value]) => value != null),
      ) as Record<string, string>;

      await uploadDataWithMetadata(signedUrl, data, contentType, headers);

      return storagePath;
    } catch (error) {
      return undefined;
    }
  }

  private async generateCustomModelDatasetItemAssetUploadUrl(
    args: Omit<GenerateUserAssetUploadUrlArgs, "assetType"> & {
      contentType: string;
    },
  ) {
    return await this.generateAssetUploadUrl({
      ...args,
      assetType: UserAssetType.CustomModelDatasetItem,
    });
  }

  private get userQuotas() {
    return getUserQuotasFromEditorContext(editorContextStore.getState());
  }

  /**
   * Uploads raw data (blob/file) into a custom model dataset item. On success, returns the storage path.
   */
  async uploadCustomModelDataItemToStorage({
    data,
    modelId,
    publicTeamId,
  }: UploadCustomModelDataItemToStorageArgs): Promise<UploadCustomModelDataItemToStorageResponse> {
    try {
      const { eventEmitter } = editorContextStore.getState();
      const currentTeamId = getCurrentTeamId();
      const userQuotas = this.userQuotas;

      if (!canUserUploadAsset(userQuotas)) {
        eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
          "ui:display-message-dialog",
          "quota-subscribe",
          {
            title: "No custom model storage space left.",
            header: "You have used all custom model storage space.",
          },
        );
        return;
      }

      const contentType = await getUploadContentType(data, data.type as EditorAssetContentType);
      const {
        assetId,
        signedUrl,
        extensionHeaders = {},
      } = await this.generateCustomModelDatasetItemAssetUploadUrl({
        contentType,
        sizeBytes: data.size,
        publicTeamId: publicTeamId || currentTeamId,
      });

      if (!signedUrl || !assetId) {
        debugError(`Cannot generate upload url for asset ${assetId} of type ${data.type}`);
        return undefined;
      }

      const storagePath = getStoragePathFromSignedUrl(signedUrl);
      debugLog(`Upload data to storage path ${storagePath}`);

      if (!storagePath) {
        return undefined;
      }

      const headers = Object.fromEntries(
        Object.entries(extensionHeaders).filter(([, value]) => value != null),
      ) as Record<string, string>;

      await uploadDataWithMetadata(signedUrl, data, contentType, headers);
      return storagePath;
    } catch (error) {
      debugError(`Cannot upload model ${modelId} data item to storage`);
    }
    return undefined;
  }

  private getPublicCustomModelsQuery() {
    return query(
      this.getCustomModelCollectionRef(),
      where("visibility", "==", DocVisibility.Public),
    );
  }

  private async getPublicCustomModelsInternal() {
    try {
      const snapshot = await getDocs(this.getPublicCustomModelsQuery());
      const models = snapshot.docs
        .map((doc) => {
          if (!doc.exists()) {
            return undefined;
          }
          const customModelInfo = doc.data();
          if (!isCustomModelInfo(customModelInfo)) {
            return undefined;
          }
          return customModelInfo;
        })
        .filter(Boolean) as CustomModelInfo[];
      return models;
    } catch (error) {
      debugError("Cannot get public custom models: ", error);
    }
    return [];
  }

  async getPublicCustomModels() {
    if (this.cachedPublicCustomModels.length > 0) {
      return this.cachedPublicCustomModels;
    }
    this.cachedPublicCustomModels = await this.getPublicCustomModelsInternal();
    return this.cachedPublicCustomModels;
  }

  private getUserCustomModelsQuery({
    publicUserId,
    publicTeamId,
  }: {
    publicUserId: PublicUserId;
    publicTeamId: PublicTeamId;
  }) {
    const queryConstraints: QueryConstraint[] = [];
    if (isPublicTeamId(publicTeamId)) {
      queryConstraints.push(where("publicTeamId", "==", publicTeamId));
    } else {
      queryConstraints.push(where(`roles.${publicUserId}`, "in", Object.values(AppRoleType)));
    }
    debugLog("getUserCustomModelsQuery constraints: ", queryConstraints);
    return query(this.getCustomModelCollectionRef(), ...queryConstraints);
  }

  onUserCustomModelsUpdate({ publicUserId, publicTeamId, callback }: OnUserCustomModelsUpdateArgs) {
    return onSnapshot(
      this.getUserCustomModelsQuery({ publicUserId, publicTeamId }),
      (customModelsSnapshot) => {
        const newModels: Record<string, CustomModelInfo> = {};
        customModelsSnapshot.docs.forEach((customModelDocRef) => {
          const project = customModelDocRef.data();
          if (isCustomModelInfo(project) && project.isDeleted !== true) {
            newModels[customModelDocRef.id] = project;
          }
        });
        callback(newModels);
      },
    );
  }

  async createCustomModel(args: CreateCustomModelArgs): Promise<HandleCreateCustomModelResponse> {
    try {
      debugLog("Create custom model args:\n", args);
      const { eventEmitter } = editorContextStore.getState();
      const userIsTeamMember = isRoleTeamMember(editorContextStore.getState());
      const userQuotas = this.userQuotas;

      if (!canUserCreateCustomModel({ userQuotas })) {
        if (userIsTeamMember) {
          eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
            "ui:display-message-dialog",
            "quota-subscribe",
            {
              title: "Contact team owner to increase quota.",
              header: "You have used all custom model credits.",
            },
          );
          return {
            ok: false,
            message: "No custom model quota left.",
          };
        } else {
          eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
            "ui:display-message-dialog",
            "quota-subscribe",
            {
              title: "No custom model credits left.",
              header: "You have used all custom model credits.",
            },
          );
          return {
            ok: false,
            message: "No custom model quota left.",
          };
        }
      }

      const currentTeamId = getCurrentTeamId();
      const response = await this.customModelActionEntrypoint({
        ...args,
        type: CustomModelAction.CreateNewModel,
        publicTeamId: currentTeamId,
      });
      const data = response.data;
      return data as HandleCreateCustomModelResponse;
    } catch (error) {
      debugError("Cannot create new custom model: ", error);
    }
    return {
      ok: false,
      message: "Unknown error when creating custom model.",
    };
  }

  async deleteCustomModel({ modelId }: DeleteCustomModelArgs): Promise<DeleteCustomModelResponse> {
    try {
      const newCustomModelDoc: Partial<CustomModelInfo> = {
        isDeleted: true,
      };
      await updateDoc(
        this.getCustomModelDocRef(modelId),
        removeUndefinedFromObject(newCustomModelDoc),
      );
      return {
        ok: true,
        message: "OK.",
      };
    } catch (error) {
      debugError(`Error when deleting custom model ${modelId}: `, error);
    }
    return {
      ok: false,
      message: "Cannot delete model.",
    };
  }

  async startCustomModelTraining(
    args: StartCustomModelTrainingArgs,
  ): Promise<HandleCustomModelTrainingStartResponse> {
    try {
      const currentTeamId = getCurrentTeamId();
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        return { ok: false, message: "Sign in before training a model." };
      }
      const fingerprint = await renderAdmissionFingerprint({
        endpoint: `${CUSTOM_MODEL_ACTION_ENTRYPOINT}:training:${args.modelId}`,
        userId,
        publicTeamId: currentTeamId ?? "personal",
        numOutputs: 1,
        body: JSON.stringify(args.trainingInput, (_key, value) =>
          value && typeof value === "object" && !Array.isArray(value)
            ? Object.fromEntries(
                Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
              )
            : value,
        ),
      });
      // Preserve an unconfirmed key across tabs/reloads, including quota denials and uncertain transport.
      const requestId = args.requestId ?? (await getRenderAdmissionRequest(fingerprint));
      if (getAuth().currentUser?.uid !== userId || getCurrentTeamId() !== currentTeamId) {
        return { ok: false, message: "Account or team changed. Retry in the selected team." };
      }
      const response = await this.customModelActionEntrypoint({
        ...args,
        requestId,
        type: CustomModelAction.StartTraining,
        publicTeamId: currentTeamId,
      });
      const data = response.data as HandleCustomModelTrainingStartResponse;
      if (
        !(data?.ok === false && typeof data.message === "string") &&
        !(
          data?.ok === true &&
          typeof data.newTrainingId === "string" &&
          /^[a-zA-Z0-9_-]{1,128}$/.test(data.newTrainingId)
        )
      ) {
        throw new Error("Invalid training acknowledgement.");
      }
      // A rejected upload ended the training under this key, so keeping the key would replay that
      // training instead of starting a new one once the image is removed.
      if (data.ok === true || data.reason === "upload_rejected") {
        try {
          await confirmRenderAdmissionRequest(fingerprint, requestId);
        } catch {
          debugError("custom_model_training_receipt_cleanup_failed");
        }
      } else {
        // Cached quota is advisory; the server must be reached to recover an already admitted training.
        const state = editorContextStore.getState();
        const canStartResponse = canUserStartTraining({
          userQuotas: this.userQuotas,
          input: args.trainingInput,
          isTeamMember: isRoleTeamMember(state),
        });
        if (!canStartResponse.ok) {
          const { title, header } =
            canStartResponse as CanUserRunCustomModelPostProcessingResponseNotAllowed;
          state.eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
            "ui:display-message-dialog",
            "quota-subscribe",
            { title, header },
          );
        }
      }
      return data;
    } catch {
      debugError("custom_model_training_submission_failed");
    }
    return { ok: false, message: "Training could not be confirmed. Retry with the same settings." };
  }

  async stopCustomModelTraining(args: StopCustomModelTrainingArgs) {
    try {
      debugLog("Stop custom model args:\n", args);
      const response = await this.customModelActionEntrypoint({
        ...args,
        type: CustomModelAction.StopTraining,
      });
      const data = response.data;
      return data as HandleCustomModelTrainingStopResponse;
    } catch (error) {
      debugError("Cannot stop new custom model: ", error);
    }
    return {
      ok: false,
      message: "Unknown error when stopping the custom model.",
    };
  }

  async startCustomModelPrediction(
    args: StartCustomModelPredictionArgs,
  ): Promise<HandleStartCustomModelPredictionResponse> {
    try {
      const { eventEmitter } = editorContextStore.getState();
      const currentTeamId = getCurrentTeamId();
      const userQuotas = this.userQuotas;

      if (
        !canUserStartPrediction({ userQuotas }) &&
        !isCustomModelPredictionInputFal(args.input) &&
        !isCustomModelPredictionInputSelfHost(args.input)
      ) {
        eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
          "ui:display-message-dialog",
          "quota-subscribe",
          {
            title: "No custom model generate credits left.",
            header: "You have used all custom model credits.",
          },
        );
        return {
          ok: false,
          message: "No custom model generate quota left.",
        };
      }

      const input = correctCustomModelPredictionInputScaleConfigs(args.input);
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        return { ok: false, message: "Sign in before generating an image." };
      }
      // Keep an unconfirmed request key across tabs/reloads; persist no prompts or tokens.
      const predictionBackend = isCustomModelPredictionInputFal(input)
        ? "fal"
        : isCustomModelPredictionInputSelfHost(input)
          ? "selfhost"
          : undefined;
      const fingerprint = predictionBackend
        ? await renderAdmissionFingerprint({
            endpoint: `${CUSTOM_MODEL_ACTION_ENTRYPOINT}:${predictionBackend}-prediction`,
            userId,
            publicTeamId: currentTeamId ?? "personal",
            numOutputs: isCustomModelPredictionInputFal(input)
              ? (input.num_images ?? 1)
              : isCustomModelPredictionInputSelfHost(input)
                ? (input.num_outputs ?? 1)
                : 1,
            body: JSON.stringify(input, (_key, value) =>
              value && typeof value === "object" && !Array.isArray(value)
                ? Object.fromEntries(
                    Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
                  )
                : value,
            ),
          })
        : undefined;
      const requestId = fingerprint
        ? (args.requestId ?? (await getRenderAdmissionRequest(fingerprint)))
        : undefined;
      if (getAuth().currentUser?.uid !== userId || getCurrentTeamId() !== currentTeamId) {
        return { ok: false, message: "Account or team changed. Retry in the selected team." };
      }
      const response = await this.customModelActionEntrypoint({
        ...args,
        input,
        ...(requestId ? { requestId } : {}),
        type: CustomModelAction.StartPrediction,
        publicTeamId: currentTeamId,
      });
      const data = response.data as HandleStartCustomModelPredictionResponse;
      if (
        !(
          data?.ok === false &&
          typeof data.message === "string" &&
          (data.submissionUncertain === undefined || typeof data.submissionUncertain === "boolean")
        ) &&
        !(data?.ok === true && typeof data.predictionId === "string" && data.predictionId)
      ) {
        throw new Error("Invalid prediction acknowledgement.");
      }
      if (data.ok === false) {
        showQuotaDialogOnDenial(data);
      }
      // A failed response can race another same-key admission; only a confirmed job clears the key.
      if (fingerprint && requestId && data.ok === true) {
        try {
          await confirmRenderAdmissionRequest(fingerprint, requestId);
        } catch {
          // Always return the authoritative response even if local receipt cleanup fails.
          debugError("custom_model_prediction_receipt_cleanup_failed");
        }
      }
      return data;
    } catch (error) {
      showQuotaDialogOnDenial(error);
      debugError("custom_model_prediction_submission_failed");
    }
    return {
      ok: false,
      message: "Unknown error when starting the custom model.",
    };
  }

  async stopCustomModelPrediction(
    args: StopCustomModelPredictionArgs,
  ): Promise<HandleStopCustomModelPredictionResponse> {
    try {
      const response = await this.customModelActionEntrypoint({
        ...args,
        type: CustomModelAction.StopPrediction,
      });
      const data = response.data;
      return data as HandleStopCustomModelPredictionResponse;
    } catch (error) {
      debugError("Cannot stop custom model training: ", error);
    }
    return {
      ok: false,
      message: "Unknown error when stopping the custom model.",
    };
  }

  async deleteCustomModelPrediction({
    predictionId,
  }: DeleteCustomModelPredictionArgs): Promise<DeleteCustomModelPredictionResponse> {
    try {
      const newCustomModelPredictionDoc: Partial<CustomModelPredictionItem> = {
        isDeleted: true,
      };
      await updateDoc(
        this.getCustomModelPredictionDocRef({
          predictionId,
        }),
        removeUndefinedFromObject(newCustomModelPredictionDoc),
      );
      return {
        ok: true,
        message: "OK.",
      };
    } catch (error) {
      debugError("Cannot delete custom model prediction: ", error);
    }
    return {
      ok: false,
      message: "Cannot delete custom model prediction.",
    };
  }

  async deleteSinglePredictionOutput({
    predictionId,
    outputUrl,
  }: DeleteSingleOutputPredictionArgs): Promise<DeleteCustomModelPredictionResponse> {
    try {
      const predictionRef = this.getCustomModelPredictionDocRef({ predictionId });
      const snapshot = await getDoc(predictionRef);
      const data = snapshot.data();

      if (!data) {
        return {
          ok: false,
          message: "Prediction not found",
        };
      }

      const newCustomModelPredictionDoc: Partial<CustomModelPredictionItem> = {
        outputDeletedMap: { ...data.outputDeletedMap, [outputUrl]: true },
      };
      await updateDoc(predictionRef, removeUndefinedFromObject(newCustomModelPredictionDoc));

      return {
        ok: true,
        message: "Output deleted successfully",
      };
    } catch (error) {
      debugError(
        `Error deleting prediction output ${outputUrl} for prediction ${predictionId}:`,
        error,
      );
      return {
        ok: false,
        message: "Failed to delete output",
      };
    }
  }

  /**
   * Monitors changes to a specific custom model prediction by doc ID.
   */
  onCustomModelPredictionUpdate({ predictionId, callback }: OnCustomModelPredictionUpdateArgs) {
    const docRef = getCustomModelPredictionDocRef({
      firestore: this.firestore,
      predictionId,
    }) as DocumentReference<CustomModelPredictionItem>;

    return onSnapshotWithExistenceCheck({
      docRef,
      onData: callback,
      typeGuard: isCustomModelPredictionItem,
      intervalMs: 2000,
    });
  }

  /**
   * Fetches the prediction item doc by ID, if it exists and is valid.
   */
  async getCustomModelPredictionItem(
    predictionId: string,
  ): Promise<CustomModelPredictionItem | undefined> {
    try {
      const customModelPredictionRef = this.getCustomModelPredictionDocRef({
        predictionId,
      });
      const snapshot = await getDoc(customModelPredictionRef);
      const data = snapshot.data();
      if (isCustomModelPredictionItem(data)) {
        return data;
      }
      return undefined;
    } catch (error) {
      debugError(`Error fetching prediction item ${predictionId}:`, error);
      return undefined;
    }
  }

  /**
   * Builds a query for user custom model predictions, optionally filtered by a specific model ID.
   * Extended to support optional limit + cursor for pagination.
   */
  private getUserCustomModelPredictionsQuery({
    publicUserId,
    publicTeamId,
    modelId,
    limit,
    cursorDoc,
  }: {
    publicUserId: PublicUserId;
    publicTeamId: PublicTeamId;
    modelId?: string;
    limit?: number;
    cursorDoc?: QueryDocumentSnapshot<CustomModelPredictionItem>;
  }) {
    const queryConstraints: QueryConstraint[] = [];

    if (publicTeamId && isPublicTeamId(publicTeamId)) {
      queryConstraints.push(where("publicTeamId", "==", publicTeamId));
    } else {
      queryConstraints.push(where(`roles.${publicUserId}`, "in", Object.values(AppRoleType)));
    }

    if (modelId) {
      queryConstraints.push(where(`usedModels.${modelId}`, "==", true));
    } else {
      queryConstraints.push(orderBy("timeCreated", "desc"));
    }

    if (cursorDoc) {
      queryConstraints.push(startAfter(cursorDoc));
    }

    if (limit) {
      queryConstraints.push(limitConstraint(limit));
    }

    return query(this.getCustomModelPredictionCollectionRef(), ...queryConstraints);
  }

  private getPublicCustomModelPredictionsQuery({ modelId }: { modelId?: string }) {
    const queryConstraints: QueryConstraint[] = [
      where("visibility", "==", DocVisibility.Public),
      where("roles", "==", {}),
    ];
    if (modelId) {
      queryConstraints.push(where(`usedModels.${modelId}`, "==", true));
    }
    return query(this.getCustomModelPredictionCollectionRef(), ...queryConstraints);
  }

  private async getPublicCustomModelPredictionsInternal({
    modelId,
  }: GetPublicCustomModelPredictionsArgs): Promise<GetPublicCustomModelPredictionsResponse> {
    const querySnapshot = await getDocs(
      this.getPublicCustomModelPredictionsQuery({
        modelId,
      }),
    );
    const outputPredictions: Record<string, CustomModelPredictionItem> = {};
    querySnapshot.docs.forEach((predictionDocRef) => {
      const predictionDoc = predictionDocRef.data();
      if (!isCustomModelPredictionItem(predictionDoc)) {
        return;
      }
      outputPredictions[predictionDocRef.id] = predictionDoc;
    });
    return outputPredictions;
  }

  async getPublicCustomModelPredictions(
    args: GetPublicCustomModelPredictionsArgs,
  ): Promise<GetPublicCustomModelPredictionsResponse> {
    return this.getPublicCustomModelPredictionsInternal(args);
  }

  /**
   * Subscribe to all predictions belonging to a single user + team, filtered by a specific model.
   * Returns an unsubscribe function.
   */
  onCustomModelPredictionsUpdate({
    publicUserId,
    publicTeamId,
    modelId,
    callback,
  }: OnCustomModelPredictionsUpdateArgs) {
    return onSnapshot(
      this.getUserCustomModelPredictionsQuery({
        publicUserId,
        publicTeamId,
        modelId,
      }),
      (customModelsSnapshot) => {
        const newPredictions: Record<string, CustomModelPredictionItem> = {};
        customModelsSnapshot.docs.forEach((predictionDocRef) => {
          const predictionDoc = predictionDocRef.data();
          if (!isCustomModelPredictionItem(predictionDoc)) {
            return;
          }
          newPredictions[predictionDocRef.id] = predictionDoc;
        });
        callback(newPredictions);
      },
    );
  }

  /**
   * Fetch all user custom model predictions, without restricting to a specific model.
   * Accepts optional pagination limit and cursor doc ID for skipping results.
   *
   * @example
   * const predictions = await manager.getAllUserCustomModelPredictions({
   *     publicUserId: "userX",
   *     publicTeamId: "teamY",
   *     limit: 20,
   *     cursorId: "somePredictionDocId"
   * });
   */
  async getAllUserCustomModelPredictions({
    publicUserId,
    publicTeamId,
    limit,
    cursorId,
  }: GetAllUserCustomModelPredictionsArgs): Promise<Record<string, CustomModelPredictionItem>> {
    let cursorDoc: QueryDocumentSnapshot<CustomModelPredictionItem> | undefined = undefined;
    if (cursorId) {
      // fetch the doc snapshot to use startAfter()
      const docRef = this.getCustomModelPredictionDocRef({ predictionId: cursorId });
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        cursorDoc = docSnap as QueryDocumentSnapshot<CustomModelPredictionItem>;
      }
    }

    const q = this.getUserCustomModelPredictionsQuery({
      publicUserId,
      publicTeamId,
      limit,
      cursorDoc,
    });
    const snapshot = await getDocs(q);
    const results: Record<string, CustomModelPredictionItem> = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (isCustomModelPredictionItem(data)) {
        results[doc.id] = data;
      }
    });
    return results;
  }

  /**
   * Subscribes to all user custom model predictions in real-time, without restricting to a single model.
   * Optionally can limit the number of docs. Returns an unsubscribe function.
   *
   * @example
   * const unsub = manager.onAllUserCustomModelPredictionsUpdate({
   *     publicUserId: "me",
   *     publicTeamId: "teamABC",
   *     limit: 50,
   *     callback: (predictions) => {
   *         console.log("Received", Object.keys(predictions).length, "predictions.");
   *     },
   * });
   */
  onAllUserCustomModelPredictionsUpdate({
    publicUserId,
    publicTeamId,
    limit,
    callback,
  }: OnAllUserCustomModelPredictionsUpdateArgs) {
    const constraints: QueryConstraint[] = [];

    if (publicTeamId && isPublicTeamId(publicTeamId)) {
      constraints.push(where("publicTeamId", "==", publicTeamId));
    } else {
      constraints.push(where(`roles.${publicUserId}`, "in", Object.values(AppRoleType)));
    }

    constraints.push(orderBy("timeCreated", "desc"));

    if (limit) {
      constraints.push(limitConstraint(limit));
    }

    const q = query(this.getCustomModelPredictionCollectionRef(), ...constraints);

    return onSnapshot(q, (snap) => {
      const newPredictions: Record<string, CustomModelPredictionItem> = {};
      snap.forEach((doc) => {
        const data = doc.data();
        if (isCustomModelPredictionItem(data)) {
          newPredictions[doc.id] = data;
        }
      });
      callback(newPredictions);
    });
  }

  async getCustomModelTrainings({
    modelId,
  }: GetCustomModelTrainingsArgs): Promise<GetCustomModelTrainingsResponse> {
    try {
      const snapshot = await getDocs(this.getCustomModelTrainingColectionRef(modelId));
      return snapshot.docs
        .map((doc) => {
          const data = doc.data();
          if (isCustomModelTrainingItem(data)) {
            return data;
          }
          return undefined;
        })
        .filter(Boolean) as GetCustomModelTrainingsResponse;
    } catch (error) {
      debugError(`Error getting custom model ${modelId} trainings: `, error);
      return undefined;
    }
  }

  async getCustomModelTraining({
    modelId,
    trainingId,
  }: GetCustomModelTrainingArgs): Promise<CustomModelTrainingItem | undefined> {
    try {
      const snapshot = await getDoc(
        this.getCustomModelTrainingDocRef({
          modelId,
          trainingId,
        }),
      );
      const data = snapshot.data();
      if (isCustomModelTrainingItem(data)) {
        return data;
      }
      return undefined;
    } catch (error) {
      debugError(`Error getting model ${modelId} training ${trainingId}: `, error);
      return undefined;
    }
  }

  onCustomModelTrainingUpdate({ modelId, trainingId, callback }: OnCustomModelTrainingUpdateArgs) {
    return onSnapshot(this.getCustomModelTrainingDocRef({ modelId, trainingId }), (snapshot) => {
      const data = snapshot.data();
      if (isCustomModelTrainingItem(data)) {
        return callback(data);
      }
    });
  }

  onCustomModelTrainingCollectionUpdate({
    modelId,
    callback,
  }: OnCustomModelTrainingCollectionUpdateArgs) {
    return onSnapshot(this.getCustomModelTrainingColectionRef(modelId), (snapshot) => {
      return callback(
        snapshot.docs
          .map((doc) => doc.exists() && doc.data())
          .filter((data) => data && isCustomModelTrainingItem(data)) as CustomModelTrainingItem[],
      );
    });
  }

  async updateUserModifedTrainingCaption(args: UpdateUserModifedTrainingCaptionArgs) {
    try {
      await updateDoc(
        this.getCustomModelTrainingDocRef({
          modelId: args.modelId,
          trainingId: args.trainingId,
        }),
        {
          userModifiedCaption: args.userModifiedCaption,
        },
      );
    } catch (error) {
      debugError("Cannot update user modified training caption: ", error);
    }
  }

  async getCustomModelInfo(modelId: string): Promise<CustomModelInfo | undefined> {
    try {
      const snapshot = await getDoc(this.getCustomModelDocRef(modelId));
      const data = snapshot.data();
      if (!isCustomModelInfo(data)) {
        return undefined;
      }
      return data;
    } catch (error) {
      debugError("Error fetching custom model info:", error);
      return undefined;
    }
  }

  async updateCustomModelInfo({ modelId, modelInfo }: UpdateCustomModelInfoArgs) {
    try {
      modelInfo = removeUndefinedFromObject(modelInfo);
      if (Object.keys(modelInfo).length <= 0) {
        debugLog(`Model ${modelId} update info is empty.`);
        return;
      }
      await updateDoc(this.getCustomModelDocRef(modelId), removeUndefinedFromObject(modelInfo));
    } catch (error) {
      debugError(`Cannot update custom model ${modelId}`, error);
    }
  }

  private async filterScaleConfigs({
    scaleConfigs,
  }: {
    scaleConfigs: CustomModelScaleConfigs;
  }): Promise<CustomModelScaleConfigs> {
    if (!scaleConfigs) {
      return {};
    }

    const results = await Promise.all(
      Object.entries(scaleConfigs).map(async ([key, config]) => {
        if (!config?.trainingId || !config?.modelId) {
          return null;
        }

        const training = await this.getCustomModelTraining({
          modelId: config.modelId,
          trainingId: config.trainingId,
        });

        return training?.input?.backendType === CustomModelTrainingBackendType.Instant
          ? null
          : [key, config];
      }),
    );

    return Object.fromEntries(results.filter(Boolean));
  }

  static CustomModelPostProcessAuthToken = import.meta.env.VITE_CUSTOM_MODEL_POST_PROCESS_API_KEY;
  static CustomModelPostProcessApiUrl = import.meta.env.VITE_CUSTOM_MODEL_POST_PROCESS_API_URL;

  // for node editor workflow
  static CustomModelPostProcessV2ApiUrl = import.meta.env.VITE_CUSTOM_MODEL_POST_PROCESS_V2_API_URL;
  static CustomModelPostProcessV2ApiKey = import.meta.env.VITE_CUSTOM_MODEL_POST_PROCESS_V2_API_KEY;

  private callCustomModelPostProcessApi = debounce(
    async ({
      userId,
      ...args
    }: CustomModelPostProcessInput & {
      userId: string;
    }) => {
      try {
        const editorState = editorContextStore.getState();
        const { eventEmitter, currentTeamId } = editorState;
        const userIsTeamMember = isRoleTeamMember(editorState);
        const userQuotas = this.userQuotas;

        const canStartResponse = canUserRunCustomModelPostProcessing({
          userQuotas,
          userIsTeamMember,
        });
        if (!canStartResponse.ok) {
          const { message, title, header } =
            canStartResponse as CanUserRunCustomModelPostProcessingResponseNotAllowed;
          eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
            "ui:display-message-dialog",
            "quota-subscribe",
            {
              title,
              header,
            },
          );
          return {
            ok: false,
            message,
          };
        }

        // Process args by filtering all scale config properties in parallel
        const processedArgs = { ...args };
        const scaleConfigProps = Object.keys(processedArgs).filter((prop) =>
          isCustomModelScaleConfigs(processedArgs[prop]),
        );

        if (scaleConfigProps.length > 0) {
          await Promise.all(
            scaleConfigProps.map(async (prop) => {
              processedArgs[prop] = await this.filterScaleConfigs({
                scaleConfigs: processedArgs[prop],
              });
            }),
          );
        }

        const response = await fetch(CustomModelManager.CustomModelPostProcessApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${CustomModelManager.CustomModelPostProcessAuthToken}`,
            UserId: userId,
            ...(await getFirebaseIdTokenHeader()),
          },
          body: JSON.stringify({ ...processedArgs, publicTeamId: currentTeamId }),
        });
        const data = (await response.json()) as CustomModelPostProcessResponse;
        return data;
      } catch (error) {
        debugError("Error when post-processing image: ", error);
        return {
          ok: false,
          message: "Unknown error when post-processing image.",
        };
      }
    },
    1000,
  );

  /** for node editor workflow cloudfunction stuffs */
  private callCustomModelPostProcessV2Api = debounce(
    async ({
      userId,
      requestType,
      ...args
    }: CustomModelPostProcessV2Input & {
      userId: string;
      requestType: CustomModelPostProcessV2ActionType;
      publicTeamId: PublicTeamId;
    }): Promise<CustomModelPostProcessV2Response> => {
      try {
        const editorState = editorContextStore.getState();
        const { eventEmitter, currentTeamId } = editorState;
        const userIsTeamMember = isRoleTeamMember(editorState);

        const userQuotas = this.userQuotas;

        const canStartResponse = canUserRunCustomModelPostProcessing({
          userQuotas,
          userIsTeamMember,
        });

        if (!canStartResponse.ok) {
          const { message, title, header } =
            canStartResponse as CanUserRunCustomModelPostProcessingResponseNotAllowed;

          eventEmitter.emit<UiDisplayMessageDialogEventHandler>(
            "ui:display-message-dialog",
            "quota-subscribe",
            {
              title,
              header,
            },
          );

          return {
            ok: false,
            message,
          };
        }
        const response = await fetch(CustomModelManager.CustomModelPostProcessV2ApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${CustomModelManager.CustomModelPostProcessV2ApiKey}`,
            UserId: userId,
            ...(await getFirebaseIdTokenHeader()),
          },
          body: JSON.stringify({ ...args, publicTeamId: currentTeamId, userId, requestType }),
        });

        const data = (await response.json()) as CustomModelPostProcessV2Response;

        return data;
      } catch (error) {
        debugError("Error when post-processing image: ", error);

        return {
          ok: false,
          message: "Unknown error when post-processing image.",
        };
      }
    },
    1000,
  );

  async getMaskFromPrompt({
    userId,
    ...args
  }: GetMaskFromPromptInput & {
    userId: string;
  }): Promise<GetMaskFromPromptResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as GetMaskFromPromptResponse;
    } catch (error) {
      debugError("Error when getting mask from prompt: ", error);
      return {
        ok: false,
        message: "Unknown error when getting mask from prompt.",
      };
    }
  }

  async changeColor({
    userId,
    ...args
  }: ChangeColorRequest & {
    userId: string;
  }): Promise<ChangeColorResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as ChangeColorResponse;
    } catch (error) {
      debugError("Error change color: ", error);
      return {
        ok: false,
        message: "",
      };
    }
  }

  async swapFace({
    userId,
    ...args
  }: SwapFaceInput & {
    userId: string;
  }): Promise<SwapFaceResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as SwapFaceResponse;
    } catch (error) {
      debugError("Error swapping face: ", error);
      return {
        ok: false,
        message: "Unknown error when swapping face.",
      };
    }
  }

  async generateVariationsInContext({
    userId,
    ...args
  }: InContextVariationsInput & {
    userId: string;
  }): Promise<InContextVariationsResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as InContextVariationsResponse;
    } catch (error) {
      debugError("Error generating variations in-context: ", error);
      return {
        ok: false,
        message: "Unknown error when generating variations.",
      };
    }
  }

  async fixProductDetails({
    userId,
    ...args
  }: FixProductDetailsInput & {
    userId: string;
  }): Promise<FixProductDetailsResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as FixProductDetailsResponse;
    } catch (error) {
      debugError("Error when fixing product details: ", error);
      return {
        ok: false,
        message: "Unknown error when getting fixing product details.",
      };
    }
  }

  async upscaleCreative({
    userId,
    ...args
  }: ClarityUpscaleInput & {
    userId: string;
  }): Promise<ClarityUpscaleResponse> {
    try {
      const response = await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      });
      return response as ClarityUpscaleResponse;
    } catch (error) {
      debugError("Error when running upscale creative: ", error);
      return {
        ok: false,
        message: "Unknown error when running upscale creative.",
      };
    }
  }

  async upscaleFace({
    userId,
    ...args
  }: FaceUpscalerInput & { userId: string }): Promise<FaceUpscalerResponse> {
    try {
      const response = await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      });
      return response as FaceUpscalerResponse;
    } catch (error) {
      debugError("Error when running face upscale: ", error);
      return {
        ok: false,
        message: "Unknown error when running face upscale.",
      };
    }
  }

  /**
   * Creates a JSON string representing the multi-node workflow graph for multi-stage generation,
   * with custom models separated between stages based on their type.
   *
   * @param args The input parameters for multi-stage generation
   * @param customModelCaptions Array of captions for the custom models
   * @param includeFashionModel Boolean indicating if a fashion model is included
   * @param styleModelConfigs Custom model configs for style models (used in first stage)
   * @param productModelConfigs Custom model configs for product models (used in second stage)
   * @returns A JSON string representing the workflow graph
   */
  private async createMultiNodeWorkflowJsonWithSeparatedModels({
    args,
    customModelCaptions,
    includeFashionModel,
    styleModelConfigs,
    productModelConfigs,
  }: {
    args: MultiStageGenerationInput;
    customModelCaptions: string[];
    includeFashionModel: boolean;
    styleModelConfigs: CustomModelScaleConfigs;
    productModelConfigs: CustomModelScaleConfigs;
  }): Promise<string> {
    const graphPayload = {
      nodes: {
        fluxGeneralNode: {
          type: "FluxGeneralNode",
          inputs: {
            prompt: args.prompt || "",
            num_inference_steps: 28,
            guidance_scale: 3.5,
            num_images: 1,
            width: args.imageSize?.width || 1024,
            height: args.imageSize?.height || 1024,
          },
          // First stage: only use style models (non-product types)
          customModels: styleModelConfigs,
        },
        getOnlyImageFromImageList: {
          type: "GetOnlyImageFromImageListNode",
        },
        multistepSegmentationNode: {
          type: "MultistepSegmentationNode",
          inputs: {
            captions: customModelCaptions,
            includeFashionModel,
          },
        },
        cleanAndDilateMaskNode: {
          type: "CleanAndDilateMaskNode",
          inputs: {
            threshold: 127,
            min_size: 100,
            dilation_size: args.maskDilationSize || 15,
            fill_holes: false,
          },
          // skipUpload: true, // example for frontend-specified skip upload. by default we don't upload this one now but you can fiddle withis and add to other nodes to skip uploading their outputs in other workflows too.
        },
        fluxInpaintingNode: {
          type: "FluxInpaintingNode",
          inputs: {
            fullPrompt: args.prompt || "",
            num_inference_steps: 28,
            guidance_scale: 3.5,
            num_images: 1,
            strength: args.secondPassStrength || 0.9,
            real_cfg_scale: 3.5,
            controlnet_inpainting_mask_threashold: 0.4,
            controlnet_inpainting_conditioning_scale:
              args.secondPassControlNetConditioningScale || 0.5,
          },
          // Second stage: use both product models and style models
          customModels: { ...styleModelConfigs, ...productModelConfigs },
        },
      },
      connections: [
        {
          fromNode: "fluxGeneralNode",
          fromOutput: "images",
          toNode: "getOnlyImageFromImageList",
          toInput: "imageList",
        },
        {
          fromNode: "getOnlyImageFromImageList",
          fromOutput: "image",
          toNode: "multistepSegmentationNode",
          toInput: "image",
        },
        {
          fromNode: "getOnlyImageFromImageList",
          fromOutput: "image",
          toNode: "fluxInpaintingNode",
          toInput: "inputImage",
        },
        {
          fromNode: "multistepSegmentationNode",
          fromOutput: "segmentationMask",
          toNode: "cleanAndDilateMaskNode",
          toInput: "inputMask",
        },
        {
          fromNode: "cleanAndDilateMaskNode",
          fromOutput: "outputMask",
          toNode: "fluxInpaintingNode",
          toInput: "inputMask",
        },
      ],
    };

    return JSON.stringify(graphPayload);
  }

  async multistepGeneration({
    userId,
    ...args
  }: MultiStageGenerationInput & {
    userId: string;
  }): Promise<MultiStageGenerationResponse> {
    try {
      // Get captions for each custom model config from firestore
      const customModelCaptions: string[] = [];
      let includeFashionModel = false;

      // Maps to separate custom models by type
      const customModelInfoMap: Record<string, CustomModelInfo> = {};
      const styleModelConfigs: CustomModelScaleConfigs = {};
      const productModelConfigs: CustomModelScaleConfigs = {};

      // Collect all model info and captions first
      if (args.customModelScaleConfigs) {
        const fetchPromises = Object.entries(args.customModelScaleConfigs).map(
          async ([key, config]) => {
            const [trainingDoc, modelInfo] = await Promise.all([
              this.getCustomModelTraining({
                modelId: config.modelId,
                trainingId: config.trainingId,
              }),
              this.getCustomModelInfo(config.modelId),
            ]);

            return { key, config, trainingDoc, modelInfo };
          },
        );

        const results = await Promise.all(fetchPromises);

        // Process the results
        for (const { key, config, trainingDoc, modelInfo } of results) {
          // Store the model info for later use
          if (modelInfo) {
            customModelInfoMap[config.modelId] = modelInfo;

            // Check if it's a fashion model
            if (modelInfo.customModelType === CustomModelType.Fashion) {
              includeFashionModel = true;
            }

            // Determine if this is a product model based on its type
            const isProductModel = CUSTOM_MODEL_PRODUCT_TYPES.has(modelInfo.customModelType);

            // Add to the appropriate config map
            if (isProductModel) {
              productModelConfigs[key] = config;
            } else {
              styleModelConfigs[key] = config;
            }
          }

          // Add caption to the list if available
          if (trainingDoc?.caption) {
            customModelCaptions.push(trainingDoc.caption);
          }
        }
      }

      // Generate the graph payload with separated model configs
      const graphPayloadJson = await this.createMultiNodeWorkflowJsonWithSeparatedModels({
        args,
        customModelCaptions,
        includeFashionModel,
        styleModelConfigs,
        productModelConfigs,
      });

      // Create a record of which models are used
      const usedModels: Record<string, boolean> = {};
      if (args.customModelScaleConfigs) {
        for (const [_, config] of Object.entries(args.customModelScaleConfigs)) {
          usedModels[config.modelId] = true;
        }
      }

      const pending = await preparePythonPrediction(
        "onCallExecuteMultiStepNodeWorkflowColabJuly24_v2",
        {
          publicTeamId: args.publicTeamId ?? getCurrentTeamId() ?? undefined,
          graphPayloadJson,
          predictionId: args.predictionId,
          predictionType: CustomModelPredictionType.MultiStageGeneration,
          usedModels,
          numExecutions: args.numImages || 1,
          useBulkGeneration: false,
          uid: userId,
          promptJson: args.promptJson,
        },
        getCurrentTeamId,
        { statusProvesCompletion: false },
      );
      const predictionId = pending.request.predictionId;
      const result = await this.executeMultiStepNodeWorkflowColabJuly24(pending.request);
      await pending.confirm(result.data);

      if (!result.data.ok) {
        debugError("Error executing multi-step node workflow:", result.data.message);
        return {
          ok: false,
          message: result.data.message || "Failed to execute multi-step node workflow",
        };
      }

      return {
        ok: true,
        predictionId,
        message: "Multi-step node workflow execution started successfully",
      };
    } catch (error) {
      showQuotaDialogOnDenial(error);
      debugError("Error calling custom model multi-step generation: ", error);
      return {
        ok: false,
        message: "Unknown error when running multi-step generation.",
      };
    }
  }

  async depthControlnet({
    userId,
    ...args
  }: DepthControlnetInput & {
    userId: string;
  }): Promise<DepthControlnetResponse> {
    try {
      const response = await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      });
      return response as DepthControlnetResponse;
    } catch (error) {
      debugError("Error when running depth controlnet: ", error);
      return {
        ok: false,
        message: "Unknown error when running depth controlnet.",
      };
    }
  }

  async customModelFixDetailsCorrespondence(
    input: FixCustomModelDetailsCorrespondenceInput,
  ): Promise<FixCustomModelDetailsCorrespondenceOutput> {
    try {
      if (!input.sourceImage) {
        return {
          ok: false,
          message: "Please provide a valid reference image.",
        };
      }
      if (!input.targetImage) {
        return {
          ok: false,
          message: "Please provide a valid target image.",
        };
      }

      const pending = await preparePythonPrediction(
        FIX_CUSTOM_MODEL_DETAILS_CORRESPONDENCE,
        { ...input, publicTeamId: input.publicTeamId ?? getCurrentTeamId() ?? undefined },
        getCurrentTeamId,
        { statusProvesCompletion: false },
      );
      const response = await this.fixCustomModelDetailsCorrespondence(pending.request);
      await pending.confirm(response.data);
      const responseData = response.data;
      if (isFixCustomModelDetailsCorrespondenceOutput(responseData)) {
        return responseData;
      }
      debugError("Invalid response from the fix details correspondence server: ", responseData);
      return {
        ok: false,
        message: "Internal server error.",
      };
    } catch (error) {
      showQuotaDialogOnDenial(error);
      debugError("Error calling custom model fix details correspondence: ", error);
      return {
        ok: false,
        message: "Internal server error.",
      };
    }
  }

  private async generateCustomModelVirtualTryOnInputAssetUploadUrl({
    contentType,
    publicTeamId,
    sizeBytes,
  }: {
    contentType: string;
    publicTeamId: PublicTeamId;
    sizeBytes: number;
  }) {
    return await this.generateAssetUploadUrl({
      contentType,
      sizeBytes,
      assetType: UserAssetType.TryOnClothImage,
      publicTeamId,
    });
  }

  async uploadCustomModelVirtualTryOnInputToStorage({
    data,
    publicTeamId,
  }: UploadCustomModelVirtualTryOnInputToStorageArgs): Promise<UploadCustomModelVirtualTryOnInputToStorageResponse> {
    try {
      const contentType = await getUploadContentType(data, data.type as EditorAssetContentType);
      const {
        assetId,
        signedUrl,
        extensionHeaders = {},
      } = await this.generateCustomModelVirtualTryOnInputAssetUploadUrl({
        contentType,
        sizeBytes: data.size,
        publicTeamId,
      });
      if (!signedUrl || !assetId) {
        return;
      }

      const storagePath = getStoragePathFromSignedUrl(signedUrl);
      if (!storagePath) {
        return undefined;
      }

      const headers = Object.fromEntries(
        Object.entries(extensionHeaders).filter(([, value]) => value != null),
      ) as Record<string, string>;

      await uploadDataWithMetadata(signedUrl, data, contentType, headers);
      return storagePath;
    } catch (error) {
      debugError("Cannot upload data to storage");
      return undefined;
    }
  }

  async callCustomModelVirtualTryOn({
    userId,
    ...args
  }: TryOnInput & {
    userId: string;
  }): Promise<TryOnResponse> {
    try {
      return (await this.callCustomModelPostProcessApi({
        ...args,
        userId,
      })) as TryOnResponse;
    } catch (error) {
      debugError("Error when getting mask from prompt: ", error);
      return {
        ok: false,
        message: "Unknown error when getting mask from prompt.",
      };
    }
  }

  /**
   * May throw an error if internal function call fails.
   */
  async updateCustomModelDatasetItemThumbnail({
    teamId,
    modelId,
    assetStoragePath,
  }: UpdateCustomModelDatasetItemThumbnailArgs): Promise<UpdateCustomModelDatasetItemThumbnailResponse> {
    const response = await this.updateCustomModelDatasetItemThumbnailColabJuly24({
      teamId,
      modelId,
      assetStoragePath,
    });
    return response.data;
  }

  autoCorrectCustomModelCaption: (
    args: AutoCorrectCustomModelCaptionArgs,
  ) => Promise<AutoCorrectCustomModelCaptionResponse> = debounce(
    async (args: AutoCorrectCustomModelCaptionArgs) => {
      try {
        const response = await this.autoCorrectCustomModelCaptionColabJuly24(args);
        return response.data;
      } catch (error) {
        debugError("Error auto-correcting custom model caption: ", error);
        return {
          ok: false,
        };
      }
    },
    1000,
  );

  async recaptionCustomModelTraining({
    modelId,
    trainingId,
  }: RecaptionCustomModelTrainingArgs): Promise<RecaptionCustomModelTrainingResponse> {
    try {
      const response = await this.recaptionCustomModelTrainingColabJuly24({
        modelId,
        trainingId,
      });
      return response.data;
    } catch (error) {
      debugError("Error recaptioning custom model training: ", error);
      return {
        ok: false,
        message: "Unknown error recaptioning custom model training.",
      };
    }
  }

  async getTrainingCaptionShortened({
    modelId,
    trainingId,
  }: GetTrainingCaptionShortenedArgs): Promise<GetTrainingCaptionShortenedResponse> {
    try {
      const response = await this.getTrainingCaptionShortenedColabJuly24({
        modelId,
        trainingId,
      });
      return response.data;
    } catch (error) {
      debugError("Error getting training caption shortened: ", error);
      return {
        ok: false,
        message: "Unknown error getting training caption shortened.",
      };
    }
  }

  async appendTextToPrompt({
    textToAdd,
    subjectToAdd,
    prompt,
    languageModel,
    triggerWordsToMaintain,
  }: AppendTextToPromptArgs): Promise<AppendTextToPromptResponse> {
    try {
      const llmPrompt = appendPromptInstructions({
        subjectToAdd,
        textToAdd,
        prompt,
        triggerWordsToMaintain,
      });
      debugLog("Calling language model: ", languageModel);
      const responseText = await this.backend?.callAnyLanguageModel({
        input: {
          model: languageModel,
          prompt: llmPrompt,
        },
      });
      debugLog("Response text from append text to prompt: ", responseText);
      if (!responseText) {
        return {
          ok: false,
          data: { modifiedPrompt: "" },
        };
      }
      const cleanedText = extractTripleTickContent(responseText) || responseText;
      const modifiedPrompt = extractModifiedPrompt(cleanedText);
      if (modifiedPrompt) {
        return {
          ok: true,
          data: {
            modifiedPrompt,
          },
        };
      }
      return null;
    } catch (error) {
      debugError("Error appending text to prompt: ", error);
      return null;
    }
  }

  async removeTextFromPrompt({
    textToRemove,
    subjectToRemove,
    prompt,
    triggerWordsToRemove,
    triggerWordsToMaintain,
    languageModel,
  }: RemoveTextFromPromptArgs): Promise<RemoveTextFromPromptResponse> {
    try {
      const llmPrompt = removePromptInstructions({
        textToRemove,
        subjectToRemove,
        prompt,
        triggerWordsToRemove,
        triggerWordsToMaintain,
      });
      debugLog("Calling language model: ", languageModel);
      const responseText = await this.backend?.callAnyLanguageModel({
        input: {
          model: languageModel,
          prompt: llmPrompt,
        },
      });
      debugLog("Response text from remove text from prompt: ", responseText);
      if (!responseText) {
        return null;
      }
      const cleanedText = extractTripleTickContent(responseText) || responseText;
      const modifiedPrompt = extractModifiedPrompt(cleanedText);
      if (modifiedPrompt) {
        return {
          ok: true,
          data: {
            modifiedPrompt,
          },
        };
      }
      return null;
    } catch (error) {
      debugError("Error removing text from prompt: ", error);
      return null;
    }
  }

  async magicEraseCustomModel(
    input: MagicEraseInput & {
      userId: string;
    },
  ): Promise<MagicEraseResponse> {
    try {
      const currentTeamId = getCurrentTeamId() ?? undefined;
      return (await this.callCustomModelPostProcessApi({
        ...input,
        publicTeamId: currentTeamId,
      })) as MagicEraseResponse;
    } catch (error) {
      debugError(
        "[magicEraseCustomModel] Error running custom model magic erase operatin: ",
        error,
      );
      return {
        ok: false,
        message: "Unknown server error.",
      };
    }
  }

  async idTextToImage(
    input: IdeogramText2ImageRequest & {
      userId: string;
    },
  ): Promise<IdeogramText2ImageResponse> {
    try {
      const currentTeamId = getCurrentTeamId() ?? undefined;
      return (await this.callCustomModelPostProcessApi({
        ...input,
        publicTeamId: currentTeamId,
      })) as IdeogramText2ImageResponse;
    } catch (error) {
      debugError("[idTextToImage] Error running id text to image operation: ", error);
      return {
        ok: false,
        message: "Unknown server error.",
      };
    }
  }

  async idInpainting(
    input: IdeogramInpaintingRequest & {
      userId: string;
    },
  ): Promise<IdeogramInpaintingResponse> {
    try {
      const currentTeamId = getCurrentTeamId() ?? undefined;
      return (await this.callCustomModelPostProcessApi({
        ...input,
        publicTeamId: currentTeamId,
      })) as IdeogramInpaintingResponse;
    } catch (error) {
      debugError("[idInpainting] Error running id inpainting operation: ", error);
      return {
        ok: false,
        message: "Unknown server error.",
      };
    }
  }

  async idGenerateVariations(
    input: IdeogramRemixRequest & {
      userId: string;
    },
  ): Promise<IdeogramRemixResponse> {
    try {
      const currentTeamId = getCurrentTeamId() ?? undefined;
      return (await this.callCustomModelPostProcessApi({
        ...input,
        publicTeamId: currentTeamId,
      })) as IdeogramInpaintingResponse;
    } catch (error) {
      debugError("[idGenerateVariations] Error running id variations operation: ", error);
      return {
        ok: false,
        message: "Unknown server error.",
      };
    }
  }

  async fcControlNet(
    input: FluxControlnetInput & {
      userId: string;
    },
  ): Promise<FluxControlnetResponse> {
    try {
      const currentTeamId = getCurrentTeamId() ?? undefined;
      return (await this.callCustomModelPostProcessApi({
        ...input,
        publicTeamId: currentTeamId,
      })) as FluxControlnetResponse;
    } catch (error) {
      debugError("[fcControlNet] Error running flux controlnet operation: ", error);
      return {
        ok: false,
        message: "Unknown server error.",
      };
    }
  }

  /**
   * Generates images using instant custom model generation with optional scale configs.
   *
   * @param args - Configuration for the instant custom model generation
   * @returns A response containing success status and prediction ID if successful
   *
   * @example
   * const response = await manager.instantCustomModelGeneration({
   *   prompt: "a beautiful landscape",
   *   promptJson: JSON.stringify({ additionalDetails: "sunny day" }),
   *   scaleConfigs: { ... },
   *   numOutputs: 1
   * });
   */
  async instantCustomModelGeneration(
    args: Omit<InstantCustomModelGenerationArgs, "type">,
  ): Promise<InstantCustomModelGenerationResponse> {
    try {
      const currentTeamId = getCurrentTeamId();
      const pending = await preparePythonPrediction(
        INSTANT_CUSTOM_MODEL_GENERATION,
        {
          ...args,
          type: CustomModelPredictionType.InstantGenerateImage,
          publicTeamId: args.publicTeamId ?? currentTeamId,
        },
        getCurrentTeamId,
      );
      const response = await this.instantCustomModelGenerationColabJuly24(pending.request);
      await pending.confirm(response.data);

      return response.data;
    } catch (error) {
      debugError("Error in instant custom model generation:", error);
      return {
        ok: false,
        message: "Unknown error during instant custom model generation.",
      };
    }
  }

  async customModelChangeCameraAngle(
    args: ChangeCameraAngleInput,
  ): Promise<ChangeCameraAngleResponse> {
    try {
      const currentTeamId = getCurrentTeamId();
      const pending = await preparePythonPrediction(
        CUSTOM_MODEL_POST_PROCESS_PYTHON,
        {
          ...args,
          type: CustomModelPostProcessActionType.ChangeCameraAngle,
          publicTeamId: args.publicTeamId ?? currentTeamId,
        },
        getCurrentTeamId,
      );
      const response = await this.customModelPostProcessPythonColabJuly24(pending.request);
      await pending.confirm(response.data);

      return response.data;
    } catch (error) {
      debugError("Error in change camera angle custom model generation:", error);
      return {
        ok: false,
        message: "Unknown error during change camera angle generation.",
      };
    }
  }

  async customModelChangePose(args: ChangePoseInput): Promise<ChangePoseResponse> {
    try {
      const currentTeamId = getCurrentTeamId();
      const pending = await preparePythonPrediction(
        CUSTOM_MODEL_POST_PROCESS_PYTHON,
        {
          ...args,
          type: CustomModelPostProcessActionType.ChangeHumanPose,
          publicTeamId: args.publicTeamId ?? currentTeamId,
        },
        getCurrentTeamId,
      );
      const response = await this.customModelPostProcessPythonColabJuly24(pending.request);
      await pending.confirm(response.data);

      return response.data;
    } catch (error) {
      debugError("Error in change pose custom model generation:", error);
      return {
        ok: false,
        message: "Unknown error during change pose generation.",
      };
    }
  }

  async relightBackground(args: ICLightInput): Promise<ICLightResponse> {
    try {
      const currentTeamId = getCurrentTeamId();
      const pending = await preparePythonPrediction(
        CUSTOM_MODEL_POST_PROCESS_PYTHON,
        {
          ...args,
          type: CustomModelPostProcessActionType.ICLight,
          publicTeamId: args.publicTeamId ?? currentTeamId,
        },
        getCurrentTeamId,
      );
      const response = await this.customModelPostProcessPythonColabJuly24(pending.request);
      await pending.confirm(response.data);

      return response.data;
    } catch (error) {
      debugError("Error in relight background:", error);
      return {
        ok: false,
        message: "Unknown error during relight background generation.",
      };
    }
  }
}
